import React, { useMemo, useState } from 'react';
import { View } from 'react-native';
import { IconButton, SegmentedButtons, Text, TextInput } from 'react-native-paper';
import { CalcShell, FormulaLine, FormulaNote, NumericField } from '../../../components/shell';
import { DiagramPitotTraverseGrid } from '../../../components/diagrams';
import { usePersistedState } from '../../../hooks/usePersistedState';
import { useUnits } from '../../../context/UnitsContext';
import { C } from '../../../theme/tokens';

const KEY = 'tabai.calc.pitotGrid.v1';

type Shape = 'round' | 'rect';

type PersistedFields = {
  shape: Shape;
  diameter: string;
  width: string;
  height: string;
  density: string;
  rows: string;
  cols: string;
};

const EMPTY: PersistedFields = {
  shape: 'round',
  diameter: '',
  width: '',
  height: '',
  density: '',
  rows: '5',
  cols: '5',
};

const AirflowPitotTraverseGridScreen = () => {
  const [fields, setFields, { clear: clearPersisted }] = usePersistedState<PersistedFields>(KEY, EMPTY);
  const [error, setError] = useState<string | null>(null);
  const { unit, toImp, fromImp, isImperial } = useUnits();

  const set = (patch: Partial<PersistedFields>) => setFields((prev) => ({ ...prev, ...patch }));

  const nPoints = (parseInt(fields.rows, 10) || 0) * (parseInt(fields.cols, 10) || 0);

  const [readings, setReadings] = useState<string[]>(Array(25).fill(''));

  React.useEffect(() => {
    setReadings((prev) => {
      if (prev.length === nPoints) return prev;
      const next = Array(nPoints).fill('');
      for (let i = 0; i < Math.min(prev.length, nPoints); i++) next[i] = prev[i];
      return next;
    });
  }, [nPoints]);

  // Area is always computed in ft² internally
  const area = useMemo(() => {
    if (fields.shape === 'round') {
      const d = toImp(fields.diameter, 'diameter'); // inches
      if (!(d > 0)) return null;
      return (Math.PI * Math.pow(d / 12, 2)) / 4;
    } else {
      const w = toImp(fields.width, 'diameter'); // inches
      const h = toImp(fields.height, 'diameter'); // inches
      if (!(w > 0) || !(h > 0)) return null;
      return (w * h) / 144;
    }
  }, [fields.shape, fields.diameter, fields.width, fields.height]);

  const computed = useMemo(() => {
    const rho = fields.density.trim() === '' ? 0.075 : toImp(fields.density, 'density');
    if (!(rho > 0)) return null;
    // Readings are in. w.g. (pressure units) — convert from display
    const vps = readings.map((r) => {
      const n = parseFloat(r);
      if (!Number.isFinite(n) || n < 0) return null;
      return toImp(String(n), 'pressure');
    }).filter((v): v is number => v !== null && v >= 0);
    if (vps.length === 0) return null;
    const factor = rho === 0.075 ? 4005 : 1096.7 / Math.sqrt(rho);
    const velocities = vps.map((vp) => factor * Math.sqrt(vp));
    const vAvg = velocities.reduce((a, b) => a + b, 0) / velocities.length;
    const cfm = area != null ? vAvg * area : null;
    const vMin = Math.min(...velocities);
    const vMax = Math.max(...velocities);
    const uniformity = vMax > 0 ? ((vMax - vMin) / vAvg) * 100 : 0;
    return { count: vps.length, vAvg, cfm, vMin, vMax, uniformity };
  }, [readings, fields.density, area]);

  const updateReading = (idx: number, v: string) => {
    setReadings((prev) => {
      const next = [...prev];
      next[idx] = v;
      return next;
    });
  };

  const clearReadings = () => setReadings(Array(nPoints).fill(''));

  const clearAll = () => {
    setError(null);
    void clearPersisted();
    setReadings(Array(nPoints).fill(''));
  };

  const rowsNum = parseInt(fields.rows, 10) || 0;
  const colsNum = parseInt(fields.cols, 10) || 0;

  const result = computed
    ? [
        { label: 'Average Velocity', value: fromImp(computed.vAvg, 'velocity', 0), unit: unit('velocity') },
        { label: 'Min / Max', value: `${fromImp(computed.vMin, 'velocity', 0)} / ${fromImp(computed.vMax, 'velocity', 0)}`, unit: unit('velocity') },
        { label: 'Uniformity (range/avg)', value: computed.uniformity.toFixed(1), unit: '%' },
        ...(area != null ? [{ label: 'Duct Area', value: fromImp(area, 'area', 3), unit: unit('area') }] : []),
        ...(computed.cfm != null
          ? [{ label: 'Flow', value: fromImp(computed.cfm, 'flow', 0), unit: unit('flow'), emphasis: true as const }]
          : []),
      ]
    : null;

  return (
    <CalcShell
      title="Pitot Traverse Grid"
      subtitle="Enter velocity-pressure readings across a grid; compute avg velocity & flow"
      formula={
        <>
          <FormulaLine>V (fpm) = 4005 · √VP  (std air)</FormulaLine>
          <FormulaLine>V = 1096.7 · √(VP / ρ)  (density-corrected)</FormulaLine>
          <FormulaLine>Flow = V_avg · A</FormulaLine>
          <FormulaNote>
            VP in {unit('pressure')}. Average is of velocities, not VPs (industry-standard method).{'\n'}
            Use 5×5 (25 pts) for rectangular or 6/10-point log-Tchebycheff for round.
          </FormulaNote>
        </>
      }
      error={error}
      onClear={clearAll}
      result={result}
      copyFooter={`Formula: V = 4005·√VP (std air); Flow = V_avg·A  (${computed?.count ?? 0} readings)`}
      diagram={<DiagramPitotTraverseGrid />}
      outputSlots={{
        area_sqft: area ?? undefined,
        air_density_lb_ft3: fields.density ? toImp(fields.density, 'density') : undefined,
        velocity_fpm: computed ? computed.vAvg : undefined,
        cfm: computed?.cfm ?? undefined,
      }}
      onPrefill={(values) => {
        const patch: Partial<PersistedFields> = {};
        if (values.air_density_lb_ft3 != null && !fields.density) {
          patch.density = fromImp(values.air_density_lb_ft3, 'density', 4);
        }
        if (Object.keys(patch).length > 0) set(patch);
      }}
    >
      <SegmentedButtons
        value={fields.shape}
        onValueChange={(v) => set({ shape: v as Shape })}
        buttons={[
          { value: 'round', label: 'Round' },
          { value: 'rect', label: 'Rectangular' },
        ]}
        style={{ marginBottom: 16 }}
      />

      {fields.shape === 'round' ? (
        <View style={{ marginBottom: 12 }}>
          <NumericField
            label="Duct Diameter"
            value={fields.diameter}
            onChangeText={(v) => set({ diameter: v })}
            rightText={unit('diameter')}
            maxDecimals={2}
          />
        </View>
      ) : (
        <>
          <View style={{ marginBottom: 12 }}>
            <NumericField
              label="Duct Width"
              value={fields.width}
              onChangeText={(v) => set({ width: v })}
              rightText={unit('diameter')}
              maxDecimals={2}
            />
          </View>
          <View style={{ marginBottom: 12 }}>
            <NumericField
              label="Duct Height"
              value={fields.height}
              onChangeText={(v) => set({ height: v })}
              rightText={unit('diameter')}
              maxDecimals={2}
            />
          </View>
        </>
      )}

      <View style={{ marginBottom: 12 }}>
        <NumericField
          label={`Air Density ρ (std=${isImperial ? '0.075 lb/ft³' : '1.2 kg/m³'})`}
          value={fields.density}
          onChangeText={(v) => set({ density: v })}
          rightText={unit('density')}
          maxDecimals={4}
        />
      </View>

      <View style={{ flexDirection: 'row', marginBottom: 12 }}>
        <View style={{ flex: 1, marginRight: 8 }}>
          <NumericField
            label="Rows"
            value={fields.rows}
            onChangeText={(v) => set({ rows: v })}
            maxDecimals={0}
          />
        </View>
        <View style={{ flex: 1 }}>
          <NumericField
            label="Cols"
            value={fields.cols}
            onChangeText={(v) => set({ cols: v })}
            maxDecimals={0}
          />
        </View>
      </View>

      <View style={{ marginBottom: 8, flexDirection: 'row', alignItems: 'center' }}>
        <Text style={{ color: '#FFFFFF', fontWeight: 'bold', flex: 1 }}>
          VP Readings ({nPoints} points, {unit('pressure')})
        </Text>
        <IconButton icon="broom" iconColor={C.accent} onPress={clearReadings} accessibilityLabel="Clear readings" />
      </View>

      {Array.from({ length: rowsNum }).map((_, r) => (
        <View key={r} style={{ flexDirection: 'row', marginBottom: 6 }}>
          {Array.from({ length: colsNum }).map((_, c) => {
            const idx = r * colsNum + c;
            return (
              <TextInput
                key={idx}
                dense
                placeholder={`${r + 1},${c + 1}`}
                value={readings[idx] ?? ''}
                onChangeText={(v) => updateReading(idx, v)}
                keyboardType="decimal-pad"
                style={{ flex: 1, marginRight: 4, backgroundColor: '#2c2c2e', height: 44 }}
                mode="outlined"
                textColor="#FFFFFF"
                placeholderTextColor="#666666"
                outlineColor="#444444"
                activeOutlineColor={C.accent}
              />
            );
          })}
        </View>
      ))}
    </CalcShell>
  );
};

export default AirflowPitotTraverseGridScreen;
